/**
 * پالت برند کارفرما
 * primary: #0C7D74 — اکشن، لینک، آیتم انتخاب‌شده
 * dark:    #070B14 — پس‌زمینه دارک، سایدبار دارک، عمق
 */

export const BRAND_PRIMARY = '#0C7D74';
export const BRAND_DARK = '#070B14';

/** teal روشن‌تر برای متن/هاور روی پس‌زمینه تیره */
export const BRAND_PRIMARY_LIGHT = '#12a095';
export const BRAND_PRIMARY_DARK = '#0a6861';
export const BRAND_PRIMARY_DEEP = '#08534e';
export const BRAND_PRIMARY_GLOW = '#1bc9bb';

export const BRAND_RGB = '12, 125, 116';
export const BRAND_DARK_RGB = '7, 11, 20';

/** سطوح تم روشن — خنثی‌تر از mint شسته تا کنتراست بوردر/متن بهتر شود */
export const LIGHT_SURFACES = {
  bg: '#f2f4f5',
  surface: '#ffffff',
  surface2: '#f0f3f3',
  elevated: '#ffffff',
  sidebar: '#ffffff',
  border: '#b8c9c6',
  borderSoft: '#cdd8d6',
};

export const LIGHT_TEXT = {
  primary: BRAND_DARK,
  secondary: '#334744',
  muted: '#4a5d5a',
  disabled: '#7a8f8b',
};

/** سطوح تم تاریک — slate-teal، نه مشکی OLED */
export const DARK_SURFACES = {
  bg: '#121a22',
  surface: '#1a2430',
  surface2: '#232f3d',
  elevated: '#2c3a4a',
  sidebar: '#161e28',
  border: '#4a6276',
  borderSoft: '#3a5062',
};

export const DANGER = {
  base: '#e11d48',
  soft: '#fff1f2',
  border: '#fecdd3',
  text: '#9f1239',
};

export const DANGER_DARK = {
  base: '#fb7185',
  soft: 'rgba(225, 29, 72, 0.16)',
  border: 'rgba(251, 113, 133, 0.38)',
  text: '#fda4af',
};

export const brandAlpha = (alpha) => `rgba(${BRAND_RGB}, ${alpha})`;
export const brandDarkAlpha = (alpha) => `rgba(${BRAND_DARK_RGB}, ${alpha})`;

/** مقادیر CSS سفارشی — در ThemeProvider روی documentElement ست می‌شود */
export function getBrandCssVariables(mode) {
  const isDark = mode === 'dark';
  const danger = isDark ? DANGER_DARK : DANGER;

  return {
    '--color-brand': isDark ? BRAND_PRIMARY_LIGHT : BRAND_PRIMARY,
    '--color-brand-hover': isDark ? BRAND_PRIMARY_GLOW : BRAND_PRIMARY_DARK,
    '--color-brand-soft': isDark ? brandAlpha(0.16) : '#e6f4f2',
    '--color-brand-muted': isDark ? brandAlpha(0.26) : '#cce9e5',
    '--color-brand-rgb': BRAND_RGB,
    '--color-dark-rgb': BRAND_DARK_RGB,
    '--color-bg': isDark ? DARK_SURFACES.bg : LIGHT_SURFACES.bg,
    '--color-surface': isDark ? DARK_SURFACES.surface : LIGHT_SURFACES.surface,
    '--color-surface-2': isDark ? DARK_SURFACES.surface2 : LIGHT_SURFACES.surface2,
    '--color-surface-elevated': isDark ? DARK_SURFACES.elevated : LIGHT_SURFACES.elevated,
    '--color-sidebar': isDark ? DARK_SURFACES.sidebar : LIGHT_SURFACES.sidebar,
    '--color-border': isDark ? DARK_SURFACES.border : LIGHT_SURFACES.border,
    '--color-border-soft': isDark ? DARK_SURFACES.borderSoft : LIGHT_SURFACES.borderSoft,
    '--color-text': isDark ? '#f0f4f3' : LIGHT_TEXT.primary,
    '--color-text-secondary': isDark ? '#c5d4d0' : LIGHT_TEXT.secondary,
    '--color-text-muted': isDark ? '#8fa8a4' : LIGHT_TEXT.muted,
    '--color-text-disabled': isDark ? '#6b8280' : LIGHT_TEXT.disabled,
    '--color-danger': danger.base,
    '--color-danger-soft': danger.soft,
    '--color-danger-border': danger.border,
    '--color-danger-text': danger.text,
    '--shadow-soft': isDark
      ? '0 4px 24px -8px rgba(0, 0, 0, 0.38)'
      : '0 4px 24px -8px rgba(7, 11, 20, 0.07)',
    '--shadow-card': isDark
      ? '0 8px 32px -8px rgba(0, 0, 0, 0.42)'
      : '0 8px 32px -12px rgba(12, 125, 116, 0.08)',
    '--shadow-glow': isDark
      ? `0 10px 40px -10px ${brandAlpha(0.38)}`
      : `0 10px 40px -10px ${brandAlpha(0.42)}`,
    '--gradient-brand': isDark
      ? `linear-gradient(135deg, ${BRAND_PRIMARY_DEEP} 0%, ${BRAND_PRIMARY} 45%, ${BRAND_PRIMARY_LIGHT} 100%)`
      : `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_PRIMARY_DARK} 52%, ${BRAND_PRIMARY_DEEP} 100%)`,
    '--gradient-brand-vivid': isDark
      ? `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_PRIMARY_LIGHT} 50%, ${BRAND_PRIMARY_GLOW} 100%)`
      : `linear-gradient(135deg, ${BRAND_PRIMARY_LIGHT} 0%, ${BRAND_PRIMARY} 50%, ${BRAND_PRIMARY_DARK} 100%)`,
    '--gradient-brand-soft': isDark
      ? `linear-gradient(135deg, ${brandAlpha(0.22)} 0%, ${brandAlpha(0.08)} 50%, transparent 100%)`
      : `linear-gradient(135deg, ${brandAlpha(0.14)} 0%, ${brandAlpha(0.05)} 50%, transparent 100%)`,
    '--gradient-danger': isDark
      ? 'linear-gradient(135deg, #be123c 0%, #e11d48 52%, #fb7185 100%)'
      : 'linear-gradient(135deg, #e11d48 0%, #be123c 52%, #9f1239 100%)',
    '--gradient-ambient': isDark
      ? `radial-gradient(ellipse 80% 60% at 100% 0%, ${brandAlpha(0.12)} 0%, transparent 55%), radial-gradient(ellipse 60% 50% at 0% 100%, ${brandAlpha(0.06)} 0%, transparent 50%), linear-gradient(180deg, ${DARK_SURFACES.bg} 0%, ${DARK_SURFACES.sidebar} 100%)`
      : `radial-gradient(ellipse 70% 55% at 100% 0%, ${brandAlpha(0.03)} 0%, transparent 50%), radial-gradient(ellipse 55% 45% at 0% 100%, rgba(255, 255, 255, 0.78) 0%, transparent 50%), linear-gradient(180deg, ${LIGHT_SURFACES.bg} 0%, #eef1f2 100%)`,
    '--scrollbar-thumb': isDark ? '#4a6160' : '#8aa8a3',
  };
}

export function applyBrandCssVariables(mode) {
  if (typeof document === 'undefined') return;
  const vars = getBrandCssVariables(mode);
  Object.entries(vars).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value);
  });
}
